<template>
    <div class="relative min-h-[500px]">
        <div v-if="loading" class="loading-overlay">
            <div class="loading-content">
                <v-progress-circular indeterminate color="#a11128" size="48"></v-progress-circular>
                <span class="mt-3 text-body-1">Loading Fire Stations...</span>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
            <div class="pb-2 flex items-center gap-4">
                <span>Last updated: {{ lastUpdateTime }}</span>
                <v-btn icon="mdi-refresh" size="small" variant="text" :loading="isRefreshing" @click="refreshData"
                    title="Refresh"></v-btn>
            </div>
            <div class="map-wrapper">
                <div id="fire-stations-map"
                    class="h-[500px] bg-gray-200 rounded-2xl flex items-center justify-center text-gray-600 text-xl">
                </div>
            </div>
            <div class="space-y-4 overflow-y-auto max-h-[500px] pr-2">
                <v-container>
                    <div class="flex h-6 mb-4">
                        <ToggleSwitch v-model="showAllSections" @update:model-value="toggleAllSections" />
                        <span class="text-sm font-medium text-gray-700 pl-2">Show All Command Centers</span>
                    </div>
                    <div v-for="commandCenter in organizedStations" :key="commandCenter.name" class="mb-6">
                        <div class="command-center-header mb-3 cursor-pointer"
                            @click="toggleCommandCenter(commandCenter.name)">
                            <div class="flex justify-between items-center">
                                <span class="text-h5 font-bold text-white leading-tight">{{ commandCenter.name }}</span>
                                <v-icon
                                    :icon="collapsedCommandCenters.includes(commandCenter.name) ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                                    class="ml-3 text-white transition-transform duration-200"
                                    style="transform: translateY(-3px);"></v-icon>
                            </div>
                        </div>

                        <div v-if="!collapsedCommandCenters.includes(commandCenter.name)"
                            class="command-center-content">
                            <div v-for="district in commandCenter.districts"
                                :key="`${commandCenter.name}-${district.number}`" class="mb-4">
                                <div class="district-header mb-2">
                                    <h3 class="text-h6 font-semibold text-secondary">District {{ district.number }}</h3>
                                </div>

                                <v-row>
                                    <v-col v-for="station in district.stations" :key="station.id"
                                        @click="selectStation(station.id, station.station)" cols="12" md="3" sm="6">
                                        <v-card variant="outlined" class="station-card">
                                            <v-card-title class="text-wrap">
                                                <b>🏢 Station {{ station.station }}</b>
                                            </v-card-title>
                                            <v-card-text class="py-1 pb-6 text-body-2">
                                                <div>{{ station.address_full }}</div>
                                                <div class="text-xs opacity-60">
                                                    <i>{{ formatMunicipality(station.municipality) }}: {{
                                                        station.ward_name
                                                    }} (Ward {{ station.ward_number }})</i>
                                                </div>
                                                <div class="text-xs opacity-60">
                                                    <i>Built: {{ station.year_built }}</i>
                                                </div>
                                            </v-card-text>
                                            <v-card-text class="py-1 text-body-2">
                                                <div class="mb-2">Units:</div>
                                                <div class="d-flex flex-wrap justify-center">
                                                    <UnitPopover v-for="unit in station.units" :key="unit"
                                                        :unit-name="unit" :incident-data="unitIncidentData(unit)"
                                                        class="mx-1 my-1" @popover-opened="handleUnitClick">
                                                        <template #trigger="{ isOpen }">
                                                            <v-chip size="small"
                                                                :variant="isUnitDispatched(unit) ? 'flat' : 'outlined'"
                                                                :color="isUnitDispatched(unit) ? 'red' : undefined"
                                                                class="units-chip" :class="{ 'popover-open': isOpen }">
                                                                {{ unit }}
                                                            </v-chip>
                                                        </template>
                                                    </UnitPopover>
                                                    <span v-if="!station.units || station.units.length === 0"
                                                        class="text-muted">
                                                        N/A
                                                    </span>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                    </div>
                </v-container>
            </div>
        </div>
    </div>
</template>

<script setup>
import axios from 'axios'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import ToggleSwitch from '@/components/ui/toggle-switch'
import UnitPopover from '@/components/ui/UnitPopover.vue'
import { useHead } from '@vueuse/head'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { trackMapInteraction, trackEvent, trackButtonClick } from '../utils/analytics.js'
import { transformFireStationsForMap } from '../utils/staticData.js'

useHead({
    title: 'Toronto Fire Stations',
    meta: [
        {
            name: 'description',
            content: 'Toronto Fire Stations: Interactive map showing all fire station locations across the city.'
        }
    ]
})

const latestIncidentPerUnitURL = process.env.VUE_APP_LATEST_INCIDENT_BY_UNIT_URL;
const apiKey = process.env.VUE_APP_API_2_KEY;

const latestIncidentByUnit = ref({})
const stations = ref([])
const mapRef = ref(null)
const markersRef = ref([])
const selectedId = ref(null)
const loading = ref(true)
const collapsedCommandCenters = ref([])
const showAllSections = ref(true)
const lastUpdateTime = ref('')
const isRefreshing = ref(false)

// Computed property to organize stations by command center and district
const organizedStations = computed(() => {
    const organized = {}

    stations.value.forEach(station => {
        const commandCenter = station.command_center || 'Unknown Command'
        const district = station.district || 'Unknown District'

        if (!organized[commandCenter]) {
            organized[commandCenter] = {
                name: commandCenter,
                districts: {}
            }
        }

        if (!organized[commandCenter].districts[district]) {
            organized[commandCenter].districts[district] = {
                number: district,
                stations: []
            }
        }

        organized[commandCenter].districts[district].stations.push(station)
    })

    // Convert to array and sort
    const commandCenters = Object.values(organized).map(commandCenter => ({
        ...commandCenter,
        districts: Object.values(commandCenter.districts).sort((a, b) => a.number - b.number)
    }))

    // Sort command centers by their lowest district number
    return commandCenters.sort((a, b) => {
        const aMinDistrict = Math.min(...a.districts.map(d => d.number))
        const bMinDistrict = Math.min(...b.districts.map(d => d.number))
        return aMinDistrict - bMinDistrict
    })
})

const formatMunicipality = (municipality) => {
    return `${municipality === 'former Toronto' ? 'Toronto' : municipality}`
}

const isUnitDispatched = (unitName) => {
    const unitData = latestIncidentByUnit.value?.[unitName]
    return unitData && !unitData.approx_end_at
}

const getStationDispatchStatus = (stationUnits) => {
    if (!stationUnits || stationUnits.length === 0) {
        return 'none'
    }

    const dispatchedUnits = stationUnits.filter(unit => isUnitDispatched(unit))

    if (dispatchedUnits.length === 0) {
        return 'none' // No units dispatched
    } else if (dispatchedUnits.length === stationUnits.length) {
        return 'all' // All units dispatched
    } else {
        return 'partial' // Some units dispatched
    }
}

const getMarkerColor = (dispatchStatus) => {
    switch (dispatchStatus) {
        case 'all':
            return '#a11128' // Red - all vehicles dispatched
        case 'none':
            return '#22c55e' // Green - no vehicles dispatched
        case 'partial':
            return '#dea061' // Orange - some vehicles dispatched
        default:
            return '#a11128' // Default to red
    }
}

const unitIncidentData = computed(() => {
    return (unitName) => latestIncidentByUnit.value?.[unitName] || null
})

const getLatestIncidentPerUnit = async () => {
    try {
        const apiResponse = await axios.get(latestIncidentPerUnitURL, {
            headers: {
                'x-api-key': apiKey
            }
        });

        if (!latestIncidentByUnit.value) {
            latestIncidentByUnit.value = {};
        }

        apiResponse.data.units.forEach(unit => {
            latestIncidentByUnit.value[unit.unit_name] = unit;
        });

        stations.value = apiResponse.data.fire_stations

        if (apiResponse.data.last_updated) {
            const date = new Date(apiResponse.data.last_updated);
            // Format as YYYY-MM-DD HH:MM:SS with 24-hour clock for consistency with other pages.
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            lastUpdateTime.value = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } else {
            lastUpdateTime.value = '';
        }
    } catch (error) {
        console.error('Error in getting latest incident per unit:', error);
    }
}

const mapCreate = async () => {
    try {
        const locations = transformFireStationsForMap(stations.value)

        // Create Leaflet map
        const map = L.map('fire-stations-map', {
            center: [43.6532, -79.3832], // Toronto (Leaflet uses [lat, lng] order)
            zoom: 10,
            zoomControl: true,
            attributionControl: true
        })

        // Add OpenStreetMap tiles (free alternative to MapLibre)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19
        }).addTo(map)

        mapRef.value = map

        const markers = []
        locations.forEach(location => {
            const [lng, lat] = location.coordinates

            const dispatchStatus = getStationDispatchStatus(location.units)
            const markerColor = getMarkerColor(dispatchStatus)

            const fireStationIcon = L.divIcon({
                className: 'fire-station-marker',
                html: `<div style="background-color: ${markerColor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
                iconSize: [12, 12],
                iconAnchor: [6, 6]
            })

            const marker = L.marker([lat, lng], { icon: fireStationIcon })
                .addTo(map)
                .bindPopup(`
                    <div style="min-width: 200px;">
                        <h3 style="margin: 0 0 8px 0; color: #a11128;">🏢 Station ${location.title}</h3>
                        <p style="margin: 4px 0;"><span style="color: ${markerColor}; font-weight: bold;">${dispatchStatus === 'all' ? 'All Units Dispatched' : dispatchStatus === 'none' ? 'All Units Cleared' : 'Some Units Dispatched'}</span></p>
                        <p style="margin: 4px 0;"><strong>Command Center:</strong> ${location.command_center}</p>
                        <p style="margin: 4px 0;"><strong>District:</strong> ${location.district}</p>
                        <p style="margin: 4px 0;"><strong>Address:</strong> ${location.address}</p>
                        <p style="margin: 4px 0;"><strong>Municipality:</strong> ${location.municipality}</p>
                        <p style="margin: 4px 0;"><strong>Ward Name:</strong> ${location.ward_name}</p>
                        <p style="margin: 4px 0;"><strong>Ward Number:</strong> ${location.ward_number}</p>
                        <p style="margin: 4px 0;"><strong>Year Built:</strong> ${location.year_built}</p>
                        <p style="margin: 4px 0;"><strong>Units:</strong></p>
                        <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px;">
                            ${location.units && location.units.length > 0
                        ? location.units.map(unit => {
                            const isDispatched = latestIncidentByUnit.value?.[unit] && !latestIncidentByUnit.value?.[unit].approx_end_at;
                            const style = isDispatched
                                ? 'background-color: #a11128; color: #ffebee; padding: 2px 8px; border-radius: 12px; font-size: 11px; border: 1px solid #c62828;'
                                : 'color: #a11128; padding: 2px 8px; border-radius: 12px; font-size: 11px; border: 1px solid #a11128;';
                            return `<span style="${style}">${unit}</span>`;
                        }).join('')
                        : '<span style="color: #666; font-style: italic;">N/A</span>'
                    }
                        </div>
                    </div>
                `)

            marker.on('click', (e) => {
                trackMapInteraction('fire_stations_map_click', {
                    coordinates: { lat: e.latlng.lat, lng: e.latlng.lng },
                    zoom_level: map.getZoom(),
                    station_id: location.id
                })
            })

            markers.push(marker)
        })

        markersRef.value = markers

        // Map event listeners
        map.on('click', (e) => {
            trackMapInteraction('fire_stations_map_click', {
                coordinates: { lat: e.latlng.lat, lng: e.latlng.lng },
                zoom_level: map.getZoom()
            })
        })

        map.on('moveend', () => {
            trackMapInteraction('fire_stations_pan', {
                center: map.getCenter(),
                bounds: map.getBounds()
            })
        })

        map.on('zoomend', () => {
            trackMapInteraction('fire_stations_zoom', {
                zoom_level: map.getZoom(),
                center: map.getCenter()
            })
        })

    } catch (error) {
        console.error('Error in fire stations map creation:', error)
        // TODO: Show user-friendly error message
    }
}

const selectStation = (stationId, stationName) => {
    selectedId.value = stationId;

    trackEvent('fire_station_selected', {
        station_id: stationId,
        station_name: stationName
    });
};

const handleUnitClick = (unitName, incidentData) => {
    trackEvent('unit_popover_opened', {
        unit_name: unitName,
        has_incident_data: !!incidentData,
        is_dispatched: incidentData && !incidentData.approx_end_at,
        incident_number: incidentData?.event_num || null
    });
};

const toggleCommandCenter = (commandCenterName) => {
    const index = collapsedCommandCenters.value.indexOf(commandCenterName)
    if (index > -1) {
        collapsedCommandCenters.value.splice(index, 1)
    } else {
        collapsedCommandCenters.value.push(commandCenterName)
    }

    trackEvent('command_center_toggle', {
        command_center: commandCenterName,
        action: index > -1 ? 'expand' : 'collapse'
    })
}

const toggleAllSections = (showAll) => {
    if (showAll) {
        collapsedCommandCenters.value = []
    } else {
        collapsedCommandCenters.value = organizedStations.value.map(cc => cc.name)
    }

    trackEvent('command_center_bulk_action', {
        action: showAll ? 'expand_all' : 'collapse_all',
        command_centers_count: organizedStations.value.length
    })
}

const refreshData = async () => {
    trackButtonClick('refresh_data', 'fire_stations_map');

    isRefreshing.value = true;
    try {
        await getLatestIncidentPerUnit();
        updateMarkerColors();
    } catch (error) {
        console.error('Error refreshing fire stations data:', error);
        trackEvent('fire_stations_data_refresh_error', {
            error_message: error.message
        });
    } finally {
        isRefreshing.value = false;
    }
};

// Watch for changes in collapsedCommandCenters to update the switch state
watch(collapsedCommandCenters, (newCollapsed) => {
    showAllSections.value = newCollapsed.length === 0
}, { deep: true })

watch(latestIncidentByUnit, () => {
    updateMarkerColors()
}, { deep: true })

const updateMarkerColors = () => {
    if (!mapRef.value || !markersRef.value) return

    markersRef.value.forEach((marker, index) => {
        const location = transformFireStationsForMap(stations.value)[index]
        if (!location) return

        const dispatchStatus = getStationDispatchStatus(location.units)
        const markerColor = getMarkerColor(dispatchStatus)

        const newIcon = L.divIcon({
            className: 'fire-station-marker',
            html: `<div style="background-color: ${markerColor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
            iconSize: [12, 12],
            iconAnchor: [6, 6]
        })

        marker.setIcon(newIcon)

        marker.setPopupContent(`
            <div style="min-width: 200px;">
                <h3 style="margin: 0 0 8px 0; color: #a11128;">🏢 Station ${location.title}</h3>
                <p style="margin: 4px 0;"><strong>Status:</strong> <span style="color: ${markerColor}; font-weight: bold;">${dispatchStatus === 'all' ? 'All Units Dispatched' : dispatchStatus === 'none' ? 'All Units Cleared' : 'Some Units Dispatched'}</span></p>
                <p style="margin: 4px 0;"><strong>Command Center:</strong> ${location.command_center}</p>
                <p style="margin: 4px 0;"><strong>District:</strong> ${location.district}</p>
                <p style="margin: 4px 0;"><strong>Address:</strong> ${location.address}</p>
                <p style="margin: 4px 0;"><strong>Municipality:</strong> ${location.municipality}</p>
                <p style="margin: 4px 0;"><strong>Ward Name:</strong> ${location.ward_name}</p>
                <p style="margin: 4px 0;"><strong>Ward Number:</strong> ${location.ward_number}</p>
                <p style="margin: 4px 0;"><strong>Year Built:</strong> ${location.year_built}</p>
                <p style="margin: 4px 0;"><strong>Units:</strong></p>
                <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px;">
                    ${location.units && location.units.length > 0
                ? location.units.map(unit => {
                    const isDispatched = latestIncidentByUnit.value?.[unit] && !latestIncidentByUnit.value?.[unit].approx_end_at;
                    const style = isDispatched
                        ? 'background-color: #a11128; color: #ffebee; padding: 2px 8px; border-radius: 12px; font-size: 11px; border: 1px solid #c62828;'
                        : 'color: #a11128; padding: 2px 8px; border-radius: 12px; font-size: 11px; border: 1px solid #a11128;';
                    return `<span style="${style}">${unit}</span>`;
                }).join('')
                : '<span style="color: #666; font-style: italic;">N/A</span>'
            }
                </div>
            </div>
        `)
    })
}

onMounted(async () => {
    try {
        await getLatestIncidentPerUnit();
        await mapCreate();
    } catch (error) {
        console.error('Error during fire stations component mount:', error);
    } finally {
        loading.value = false;
    }
})

onUnmounted(() => {
    if (mapRef.value) {
        mapRef.value.remove();
    }
})
</script>

<style scoped>
.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
}

#fire-stations-map {
    z-index: 0;
    width: 100%;
    height: 40vh;
    min-height: 250px;
    max-height: 500px;
    margin-bottom: 2rem;
    border: 1px solid #ccc;
    border-radius: 8px;
}

.map-wrapper {
    width: 100%;
    margin: 0 auto;
    padding: 0 1rem;
}

.station-card {
    border-left: 4px solid #a11128;
}

.command-center-header {
    background: linear-gradient(135deg, #a11128 0%, #c62828 100%);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(161, 17, 40, 0.2);
    transition: all 0.2s ease;
}

.command-center-header:hover {
    background: linear-gradient(135deg, #c62828 0%, #d32f2f 100%);
    box-shadow: 0 4px 8px rgba(161, 17, 40, 0.3);
}

.command-center-header h2 {
    margin: 0;
    color: white !important;
}

.command-center-content {
    animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.district-header {
    background: #f5f5f5;
    border-left: 4px solid #a11128;
    padding: 8px 12px;
    border-radius: 4px;
    margin-left: 8px;
}

.district-header h3 {
    margin: 0;
    color: #333 !important;
}

/* Leaflet marker styles */
:deep(.fire-station-marker) {
    background: transparent;
    border: none;
}

:deep(.leaflet-popup-content-wrapper) {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.leaflet-popup-content) {
    margin: 12px;
    line-height: 1.4;
}

:deep(.leaflet-popup-tip) {
    background: white;
}

.units-chip {
    color: #a11128;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
}

/* Styling for dispatched units (flat variant) */
.units-chip.v-chip--variant-flat {
    background-color: #a11128 !important;
    color: #ffebee !important;
    border: 1px solid #c62828 !important;
}

/* Styling for popover open state */
.units-chip.popover-open {
    box-shadow: 0 0 0 2px rgba(161, 17, 40, 0.3) !important;
    transform: scale(1.05);
    transition: all 0.2s ease;
}

@media (min-width: 768px) {
    .map-wrapper {
        width: 90%;
        max-width: 1000px;
        padding: 0;
    }
}
</style>